<template>
  <mf-card-container v-model="searchFilter" title="Histórico" use-search placeholder="Buscar pelo consumidor ou produto">
    <template v-slot:filters>
      <v-tooltip :disabled="hasPiiAccess" max-width="250" top content-class="truncated-tooltip">
        <template v-slot:activator="{ on }">
          <span v-on="on">
            <export-cashback-distribution-dialog :filters="filtersVariable" :disabled="!hasPiiAccess" />
          </span>
        </template>
        <span>Você precisa ter a permissão de acesso a informações privilegiadas para acessar esse recurso.</span>
      </v-tooltip>
    </template>
    <paginated-data-table
      ref="table"
      :headers="headers"
      :search="searchFilter"
      :query="query"
      :custom-items-per-page="10"
      queryName="tabularDistributedCashback"
      queryMainPropertyName="data"
      :queryVariables="queryVariables"
      :graphql-uri="$microservicesUrls['cashback']"
      default-table-sort-by="DATA"
      :default-table-sort-desc="true"
      @update-options="updateVariables"
    >
      <template v-slot:item="{ item, index }">
        <tr>
          <td>
            <router-link v-if="hasPiiAccess" class="link" :to="{ path: `/clientes/detalhes-cliente/${item.customer.COD_ID_CLIENTE}` }">
              {{ item.customer.NUM_CPF_CNPJ | cpfCnpj }}
            </router-link>
            <v-tooltip v-else max-width="250" top content-class="truncated-tooltip">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  {{ item.customer.NUM_CPF_CNPJ }}
                </span>
              </template>
              <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
            </v-tooltip>
          </td>
          <td>
            <v-tooltip v-model="showTooltipProductName[index]" max-width="250" top content-class="truncated-tooltip">
              <template v-slot:activator="{ on }">
                <router-link v-if="item.product.DES_PRODUTO" class="link" :to="{ path: `/produtos/detalhes-produto/${item.product.COD_ID_PRODUTO}` }">
                  <span v-on="on">
                    {{ item.product.DES_PRODUTO | truncate({ length: 150 }) | hifen }}
                  </span>
                </router-link>
                <span v-else>-</span>
              </template>
              <span>{{ item.product.DES_PRODUTO | hifen }}</span>
            </v-tooltip>
          </td>
          <td>{{ item.product.NUM_UNIDADE_COM_CASHBACK | hifen }}</td>
          <td>
            <v-tooltip v-model="showTooltipStoreName[index]" max-width="250" top content-class="truncated-tooltip">
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ item.store.NOM_NOME | truncate({ length: 150 }) | hifen }}</span>
              </template>
              <span>{{ item.store.NOM_NOME | hifen }}</span>
            </v-tooltip>
          </td>
          <td>{{ formatCurrency(item.cashback) | hifen }}</td>
          <td>{{ formattedDate(item.date) }}</td>
          <td>{{ formattedDate(item.expires_at) | hifen }}</td>
        </tr>
      </template>
    </paginated-data-table>
  </mf-card-container>
</template>

<script>
import { mapState } from 'vuex'
import ExportCashbackDistributionDialog from '@/modules/mobile/cashback/views/components/ExportCashbackDistributionDialog.vue'
import { TABULAR_DISTRIBUTED_CASHBACK } from '@/modules/mobile/cashback/graphql'
import { assertArray } from '@/config/helpers.js'
import { DateTime } from 'luxon'

export default {
  name: 'DistributedCashbackMetrics',
  components: {
    ExportCashbackDistributionDialog,
    PaginatedDataTable: () => import('@/components/PaginatedDataTable.vue'),
  },
  data() {
    return {
      searchFilter: '',
      newQueryVariables: {
        sort: [],
      },
      showTooltipProductName: {},
      showTooltipStoreName: {},
      exportingCsv: false,
    }
  },
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),

    filtersVariable() {
      const { data, units, products } = this.stateFilters
      const [startDate, endDate] = assertArray(data)
      const filters = []

      if (startDate) filters.push({ type: 'DATE', value: startDate, predicate: 'GREATER_THAN_OR_EQUALS' })
      if (endDate) filters.push({ type: 'DATE', value: endDate, predicate: 'LESS_THAN_OR_EQUALS' })
      if (assertArray(units).length) filters.push({ type: 'STORE', value: units, predicate: 'IN' })
      if (assertArray(products).length) filters.push({ type: 'PRODUCT', value: products, predicate: 'IN' })

      return filters
    },

    queryVariables() {
      const variables = {
        filters: this.filtersVariable,
      }

      if (this.newQueryVariables.sort.length) {
        variables.sort = this.newQueryVariables.sort
      }

      return variables
    },

    headers() {
      return [
        {
          text: 'CPF',
          value: 'customer.NUM_CPF_CNPJ',
          width: '170px',
          sortable: true,
        },
        {
          text: 'Produto',
          value: 'product.DES_PRODUTO',
          width: '18%',
          sortable: true,
        },
        {
          text: 'Unidades com cashback',
          value: 'product.NUM_UNIDADE_COM_CASHBACK',
          width: '17%',
          sortable: true,
        },
        {
          text: 'Loja',
          value: 'store.NOM_NOME',
          width: '15%',
          sortable: true,
        },
        {
          text: 'Cashback adquirido',
          value: 'cashback',
          width: '180px',
          sortable: true,
        },
        {
          text: 'Data da compra',
          value: 'DATA',
          width: '17%',
          sortable: true,
        },
        {
          text: 'Expiração do cashback',
          value: 'EXPIRATION',
          width: '15%',
          sortable: true,
        },
      ]
    },

    query() {
      return TABULAR_DISTRIBUTED_CASHBACK
    },

    hasPiiAccess() {
      return this.$hasPiiAccess()
    },
  },
  methods: {
    formatCurrency(currency) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(currency)
    },

    updateVariables(optionsVariables) {
      const { sortBy } = optionsVariables

      if (sortBy.length) {
        const labelsSortBy = {
          'customer.NUM_CPF_CNPJ': 'CPF',
          'product.DES_PRODUTO': 'PRODUTO',
          'product.NUM_UNIDADE_COM_CASHBACK': 'CASHBACK',
          'store.NOM_NOME': 'STORE',
          cashback: 'TOTAL_CASHBACK',
          date: 'DATA',
        }

        this.newQueryVariables.sort = sortBy.map((sortItem) => labelsSortBy?.[sortItem]).filter((sortItem) => sortItem)
      } else {
        this.newQueryVariables.sort = []
      }
    },

    formattedDate(date) {
      if (!date) return '-'
      return DateTime.fromISO(date).setZone('UTC-3').toFormat('dd/MM/yyyy')
    },
  },
}
</script>
<style lang="scss" scoped>
.blue-action-text {
  color: $marca-0-l40;
  text-decoration: underline;
  font-size: 14px;
  font-weight: 400;
  font-family: 'Lato';
  text-align: left;
  &:hover {
    cursor: pointer;
  }
}
.search-container {
  width: 100%;
}
.report-icon {
  bottom: 7px;
}
</style>
